import { useState, useEffect, useCallback, useMemo } from 'react'
import { glorifyApiClient, GlorifyApiError } from '../../../services/api/glorify'
import type { Client, CreateClientRequest } from '../../../services/api/glorify'
import { toSectionError, type SectionError } from './marketsTheme'
import { SectionErrorBanner } from './marketsShared'

// ─── Client create/edit form ────────────────────────────────────────────────

interface ClientFormState {
  client_name: string
  client_email: string
  client_phone: string
  client_abn: string
  billing_address_line1: string
  billing_address_line2: string
  billing_city: string
  billing_state: string
  billing_postcode: string
  billing_country: string
  notes: string
}

const BLANK_FORM: ClientFormState = {
  client_name: '',
  client_email: '',
  client_phone: '',
  client_abn: '',
  billing_address_line1: '',
  billing_address_line2: '',
  billing_city: '',
  billing_state: '',
  billing_postcode: '',
  billing_country: 'Australia',
  notes: '',
}

function formStateFromClient(c: Client): ClientFormState {
  return {
    client_name: c.client_name,
    client_email: c.client_email ?? '',
    client_phone: c.client_phone ?? '',
    client_abn: c.client_abn ?? '',
    billing_address_line1: c.billing_address_line1 ?? '',
    billing_address_line2: c.billing_address_line2 ?? '',
    billing_city: c.billing_city ?? '',
    billing_state: c.billing_state ?? '',
    billing_postcode: c.billing_postcode ?? '',
    billing_country: c.billing_country,
    notes: c.notes ?? '',
  }
}

/** Trims and converts blank strings to `undefined` so optional fields aren't sent as empty strings. */
function formStateToRequest(form: ClientFormState): CreateClientRequest {
  const opt = (v: string): string | undefined => (v.trim() === '' ? undefined : v.trim())
  return {
    client_name: form.client_name.trim(),
    client_email: opt(form.client_email),
    client_phone: opt(form.client_phone),
    client_abn: opt(form.client_abn),
    billing_address_line1: opt(form.billing_address_line1),
    billing_address_line2: opt(form.billing_address_line2),
    billing_city: opt(form.billing_city),
    billing_state: opt(form.billing_state),
    billing_postcode: opt(form.billing_postcode),
    billing_country: opt(form.billing_country),
    notes: opt(form.notes),
  }
}

interface ClientFormProps {
  initial: Client | null
  onCancel: () => void
  onSaved: () => void
}

function ClientForm({ initial, onCancel, onSaved }: ClientFormProps): React.JSX.Element {
  const [form, setForm] = useState<ClientFormState>(initial ? formStateFromClient(initial) : BLANK_FORM)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<SectionError | null>(null)

  const set = (patch: Partial<ClientFormState>): void => setForm((prev) => ({ ...prev, ...patch }))

  const handleSubmit = async (): Promise<void> => {
    if (form.client_name.trim() === '') {
      setError({ kind: 'error', message: 'Client name is required.' })
      return
    }
    setIsSaving(true)
    setError(null)
    try {
      const payload = formStateToRequest(form)
      if (initial) {
        await glorifyApiClient.updateClient(initial.client_id, payload)
      } else {
        await glorifyApiClient.createClient(payload)
      }
      onSaved()
    } catch (err) {
      setError(toSectionError(err, 'Failed to save client'))
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="gd-card gd-card--outlined" style={{ marginBottom: 20 }}>
      <h2 className="gd-section__title" style={{ marginTop: 0 }}>{initial ? `Edit ${initial.client_name}` : 'New Client'}</h2>
      <SectionErrorBanner error={error} />

      <div className="gd-setup-row">
        <div className="gd-field">
          <label htmlFor="gd-client-name">Name *</label>
          <input id="gd-client-name" type="text" value={form.client_name} onChange={(e) => set({ client_name: e.target.value })} />
        </div>
        <div className="gd-field">
          <label htmlFor="gd-client-email">Email</label>
          <input id="gd-client-email" type="email" value={form.client_email} onChange={(e) => set({ client_email: e.target.value })} />
        </div>
        <div className="gd-field">
          <label htmlFor="gd-client-phone">Phone</label>
          <input id="gd-client-phone" type="text" value={form.client_phone} onChange={(e) => set({ client_phone: e.target.value })} />
        </div>
        <div className="gd-field">
          <label htmlFor="gd-client-abn">ABN</label>
          <input id="gd-client-abn" type="text" value={form.client_abn} onChange={(e) => set({ client_abn: e.target.value })} />
        </div>
      </div>

      <div className="gd-setup-row">
        <div className="gd-field" style={{ flex: 1, minWidth: 200 }}>
          <label htmlFor="gd-client-addr1">Address line 1</label>
          <input id="gd-client-addr1" type="text" value={form.billing_address_line1} onChange={(e) => set({ billing_address_line1: e.target.value })} />
        </div>
        <div className="gd-field" style={{ flex: 1, minWidth: 200 }}>
          <label htmlFor="gd-client-addr2">Address line 2</label>
          <input id="gd-client-addr2" type="text" value={form.billing_address_line2} onChange={(e) => set({ billing_address_line2: e.target.value })} />
        </div>
      </div>

      <div className="gd-setup-row">
        <div className="gd-field">
          <label htmlFor="gd-client-city">City</label>
          <input id="gd-client-city" type="text" value={form.billing_city} onChange={(e) => set({ billing_city: e.target.value })} />
        </div>
        <div className="gd-field">
          <label htmlFor="gd-client-state">State</label>
          <input id="gd-client-state" type="text" value={form.billing_state} onChange={(e) => set({ billing_state: e.target.value })} />
        </div>
        <div className="gd-field">
          <label htmlFor="gd-client-postcode">Postcode</label>
          <input id="gd-client-postcode" type="text" value={form.billing_postcode} onChange={(e) => set({ billing_postcode: e.target.value })} />
        </div>
        <div className="gd-field">
          <label htmlFor="gd-client-country">Country</label>
          <input id="gd-client-country" type="text" value={form.billing_country} onChange={(e) => set({ billing_country: e.target.value })} />
        </div>
      </div>

      <div className="gd-setup-row">
        <div className="gd-field" style={{ flex: 1, minWidth: 200 }}>
          <label htmlFor="gd-client-notes">Notes</label>
          <input id="gd-client-notes" type="text" value={form.notes} onChange={(e) => set({ notes: e.target.value })} />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <button className="gd-btn gd-btn--filled" disabled={isSaving} onClick={() => void handleSubmit()}>
          {isSaving ? 'Saving…' : initial ? 'Save changes' : 'Create client'}
        </button>
        <button className="gd-btn gd-btn--text" disabled={isSaving} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  )
}

// ─── Page ────────────────────────────────────────────────────────────────────

type ActiveFilter = 'all' | 'active' | 'inactive'

export default function ClientsPage(): React.JSX.Element {
  const [clients, setClients] = useState<Client[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<SectionError | null>(null)
  const [filter, setFilter] = useState<ActiveFilter>('active')
  const [formTarget, setFormTarget] = useState<'new' | Client | null>(null)
  const [togglingIds, setTogglingIds] = useState<Set<number>>(new Set())
  const [actionError, setActionError] = useState<string | null>(null)

  const fetchClients = useCallback(async (): Promise<void> => {
    setLoadError(null)
    try {
      const resp = await glorifyApiClient.listClients()
      setClients(resp.clients)
    } catch (err) {
      setLoadError(toSectionError(err, 'Failed to load clients'))
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchClients()
  }, [fetchClients])

  useEffect(() => {
    if (!actionError) return
    const timer = setTimeout(() => setActionError(null), 4000)
    return () => clearTimeout(timer)
  }, [actionError])

  const filteredClients = useMemo((): Client[] => {
    switch (filter) {
      case 'active': return clients.filter((c) => c.is_active)
      case 'inactive': return clients.filter((c) => !c.is_active)
      default: return clients
    }
  }, [clients, filter])

  const handleFormSaved = (): void => {
    setFormTarget(null)
    void fetchClients()
  }

  const handleActiveToggle = async (client: Client): Promise<void> => {
    setTogglingIds((prev) => new Set(prev).add(client.client_id))
    const snapshot = clients
    setClients((prev) => prev.map((c) => (c.client_id === client.client_id ? { ...c, is_active: !c.is_active } : c)))
    try {
      const updated = await glorifyApiClient.updateClient(client.client_id, { is_active: !client.is_active })
      setClients((prev) => prev.map((c) => (c.client_id === client.client_id ? updated : c)))
    } catch (err) {
      setClients(snapshot)
      setActionError(err instanceof GlorifyApiError ? err.message : 'Failed to update client status')
    } finally {
      setTogglingIds((prev) => {
        const next = new Set(prev)
        next.delete(client.client_id)
        return next
      })
    }
  }

  return (
    <>
      <div className="gd-page-header gd-page-header--with-action">
        <div className="gd-page-header__text">
          <h1>Clients</h1>
          <p>External billing contacts invoices are issued against.</p>
        </div>
        <button className="gd-btn gd-btn--filled has-icon" onClick={() => setFormTarget('new')}>
          <span className="material-icons" aria-hidden="true">person_add</span>
          New Client
        </button>
      </div>

      {formTarget && (
        <ClientForm
          initial={formTarget === 'new' ? null : formTarget}
          onCancel={() => setFormTarget(null)}
          onSaved={handleFormSaved}
        />
      )}

      {actionError && (
        <div className="gd-action-error" role="alert">
          <span className="material-icons" aria-hidden="true">error_outline</span>
          {actionError}
        </div>
      )}
      <SectionErrorBanner error={loadError} />

      <div className="gd-segmented" role="group" aria-label="Filter clients by active status">
        {(['active', 'inactive', 'all'] as ActiveFilter[]).map((f) => (
          <button
            key={f}
            className={`gd-segmented__btn${filter === f ? ' gd-segmented__btn--active' : ''}`}
            onClick={() => setFilter(f)}
            aria-pressed={filter === f}
          >
            {f === 'active' ? 'Active' : f === 'inactive' ? 'Inactive' : 'All'}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="gd-empty">
          <div className="gd-spinner" aria-label="Loading clients" />
        </div>
      ) : filteredClients.length === 0 ? (
        <div className="gd-empty">
          <span className="material-icons" aria-hidden="true">contacts</span>
          <p>No {filter === 'all' ? '' : `${filter} `}clients found.</p>
        </div>
      ) : (
        <div className="gd-table-wrap">
          <table className="gd-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>ABN</th>
                <th>Location</th>
                <th>Active</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {filteredClients.map((c) => (
                <tr key={c.client_id}>
                  <td><b>{c.client_name}</b></td>
                  <td className="gd-cell-muted">{c.client_email ?? '—'}</td>
                  <td className="gd-cell-muted">{c.client_abn ?? '—'}</td>
                  <td className="gd-cell-muted">
                    {[c.billing_city, c.billing_state].filter(Boolean).join(', ') || '—'}
                  </td>
                  <td>
                    <label
                      className={['gd-switch', c.is_active ? 'gd-switch--on' : '', togglingIds.has(c.client_id) ? 'gd-switch--disabled' : '']
                        .filter(Boolean)
                        .join(' ')}
                      aria-label={`Toggle active status for ${c.client_name}`}
                    >
                      <input
                        type="checkbox"
                        className="gd-switch__input"
                        checked={c.is_active}
                        disabled={togglingIds.has(c.client_id)}
                        onChange={() => void handleActiveToggle(c)}
                      />
                      <span className="gd-switch__track" aria-hidden="true">
                        <span className="gd-switch__thumb" />
                      </span>
                    </label>
                  </td>
                  <td>
                    <button
                      className="gd-btn gd-btn--outlined"
                      style={{ height: 34, fontSize: 13, padding: '0 16px' }}
                      onClick={() => setFormTarget(c)}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}
